import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAxios } from './AuthContext';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    // Check localStorage first for immediate load
    const saved = localStorage.getItem('calvio_theme');
    if (saved) return saved;
    
    // Check system preference
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return 'dark';
    }
    return 'light';
  });

  // Apply theme to document
  useEffect(() => {
    const root = document.documentElement;
    
    if (theme === 'dark') {
      root.classList.add('dark');
      root.style.setProperty('--bg-primary', '#1a1a2e');
      root.style.setProperty('--bg-secondary', '#16213e');
      root.style.setProperty('--text-primary', '#e4e4e7');
      root.style.setProperty('--text-secondary', '#a1a1aa');
      root.style.setProperty('--border-color', '#374151');
    } else {
      root.classList.remove('dark');
      root.style.setProperty('--bg-primary', '#FFFBF5');
      root.style.setProperty('--bg-secondary', '#ffffff');
      root.style.setProperty('--text-primary', '#2D3748');
      root.style.setProperty('--text-secondary', '#718096');
      root.style.setProperty('--border-color', '#e5e7eb');
    }
    
    localStorage.setItem('calvio_theme', theme);
  }, [theme]);

  // Listen for system theme changes when in auto mode
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handleChange = (e) => {
      const savedTheme = localStorage.getItem('calvio_theme_preference');
      if (savedTheme === 'auto') {
        setTheme(e.matches ? 'dark' : 'light');
      }
    };
    
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // Load theme preference from backend when user is authenticated
  const loadThemeFromSettings = async () => {
    try {
      const response = await authAxios.get('/settings');
      const preference = response.data?.theme || 'light';
      localStorage.setItem('calvio_theme_preference', preference);
      
      if (preference === 'auto') {
        const systemDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
        setTheme(systemDark ? 'dark' : 'light');
      } else {
        setTheme(preference);
      }
    } catch (error) {
      // User not authenticated or error - use localStorage
      console.log('Could not load theme from settings');
    }
  };

  const updateTheme = (newTheme) => {
    localStorage.setItem('calvio_theme_preference', newTheme);
    
    if (newTheme === 'auto') {
      const systemDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      setTheme(systemDark ? 'dark' : 'light');
    } else {
      setTheme(newTheme);
    }
  };

  const value = {
    theme,
    setTheme: updateTheme,
    loadThemeFromSettings,
    isDark: theme === 'dark'
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeContext;
